import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import EventDetail from "../../components/modules/AuthActivity/EventDetail";
import { AdapidActivityList } from "../../components/modules/AuthActivity/AdapidActivityList/AdapidActivityList";
import { AdapidAppLockerActivityList } from "../../components/modules/AdapidMFA/AdapidAppLockerActivityList/AdapidAppLockerActivityList";

import "../AuthActivity/AuthActivityPage.css";
import Pagination from "../../components/common/Pagination/Pagination";
import { Loading, SearchBox } from "../../components/common";

// Unified Redux actions from correct slice source
import {
  fetchAdapidActivities,
  fetchAdapidAppLockerActivities,
  fetchAdapidMfaOverview,
  setSelectedEvent,
  clearSelectedEvent,
  setCurrentPage,
  setAdapidSearchQuery,
  setAdapidStatusFilter,
  setActiveTab,
} from "../../store/slices/adapIdMfaSlice";
import MfaOverviewStats from "../../components/modules/AdapidMFA/MfaOverviewStats/MfaOverviewStats";

const AdapidMFA = () => {
  const dispatch = useDispatch();

  // Connected to the accurate slice root selector
  const {
    currentTab,
    adapidActivities,
    adapidAppLockerActivities,
    selectedEvent,
    detailLoading,
    currentPage,
    pageSize,
    searchQuery,
    statusFilter,
    data: stats,
    loading: statsLoading,
  } = useSelector((state) => state.adapidMfa);

  // Determine what dataset to loop through depending on active tab selection
  const currentActivityState =
    currentTab === "behavioral" ? adapidActivities : adapidAppLockerActivities;

  const loadCurrentData = useCallback(() => {
    const payload = {
      page: currentPage,
      limit: pageSize,
      search: searchQuery || undefined,
      status: statusFilter || undefined,
    };

    if (currentTab === "behavioral") {
      dispatch(fetchAdapidActivities(payload));
    } else {
      dispatch(fetchAdapidAppLockerActivities(payload));
    }
  }, [dispatch, currentTab, currentPage, pageSize, searchQuery, statusFilter]);

  // Handle data fetching variations
  useEffect(() => {
    loadCurrentData();
  }, [loadCurrentData]);

  useEffect(() => {
    dispatch(fetchAdapidMfaOverview());
  }, [dispatch]);

  const handleRefresh = useCallback(() => {
    dispatch(setAdapidSearchQuery(""));
    dispatch(setAdapidStatusFilter(""));

    const basePayload = { page: 1, limit: pageSize, forceRefresh: true };
    if (currentTab === "behavioral") {
      dispatch(fetchAdapidActivities(basePayload));
    } else {
      dispatch(fetchAdapidAppLockerActivities(basePayload));
    }
  }, [dispatch, currentTab, pageSize]);

  const handleSearchChange = useCallback(
    (value) => {
      dispatch(setAdapidSearchQuery(value));
    },
    [dispatch],
  );

  const handleStatusChange = (e) => {
    dispatch(setAdapidStatusFilter(e.target.value));
  };

  const handleTabChange = (tabName) => {
    dispatch(setActiveTab(tabName));
  };

  const handleSelectEvent = useCallback(
    (event) => {
      dispatch(setSelectedEvent(event));
    },
    [dispatch],
  );

  const handleCloseDetail = useCallback(() => {
    dispatch(clearSelectedEvent());
  }, [dispatch]);

  const handlePageChange = useCallback(
    (newPage) => {
      dispatch(setCurrentPage(newPage));
    },
    [dispatch],
  );

  const { displayData, paginationInfo, isLoading } = useMemo(() => {
    return {
      displayData: currentActivityState.data,
      isLoading: currentActivityState.loading,
      paginationInfo: {
        page: currentPage,
        totalPages: currentActivityState.pagination?.totalPages || 0,
        totalCount: currentActivityState.pagination?.totalCount || 0,
        pageSize: pageSize,
      },
    };
  }, [currentActivityState, currentPage, pageSize]);

  return (
    <div className="auth-activity-page">
      <div className="auth-activity-page__header">
        <div>
          <h1 className="auth-activity-page__title">
            AdapID Identity Intelligence
          </h1>
          <p className="auth-activity-page__subtitle">
            Real-time feed of authentication event verification layers and
            anomaly flags
          </p>
        </div>
        <div className="auth-activity-page__actions">
          <SearchBox
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={`Search ${currentTab === "behavioral" ? "Behavioral" : "AppLocker"} logs...`}
            className="activity-search"
          />

          <button
            className="refresh-button"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {isLoading ? "Refreshing..." : "↻ Refresh"}
          </button>
        </div>
      </div>

      <MfaOverviewStats stats={stats} loading={statsLoading} />

      {/* Tab and filter control bar layout */}
      <div
        className="auth-activity-page__tabs-container"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "20px 0",
        }}
      >
        <div className="auth-activity-tabs">
          <button
            className={`tab-button ${
              currentTab === "behavioral" ? "active-tab" : ""
            }`}
            onClick={() => handleTabChange("behavioral")}
          >
            AdapID Activities
          </button>

          <button
            className={`tab-button ${
              currentTab === "applocker" ? "active-tab" : ""
            }`}
            onClick={() => handleTabChange("applocker")}
          >
            AppLocker Activity
          </button>
        </div>

        <div className="auth-activity-page__actions">
          <select
            className="status-filter-dropdown"
            value={statusFilter}
            onChange={handleStatusChange}
            style={{ padding: "8px 12px", borderRadius: "4px" }}
          >
            <option value="">All Statuses</option>
            <option value="Profile created">Profile Created</option>
            <option value="Insufficient data">Insufficient Data</option>
            <option value="Step up required">Suspicious User</option>
            <option value="Legitimate user">Legitimate User</option>
          </select>
        </div>
      </div>

      <div
        className={`auth-activity-page__content ${selectedEvent ? "auth-activity-page__content--with-detail" : ""}`}
      >
        <div className="auth-activity-page__list">
          {isLoading ? (
            <Loading
              text={`Fetching ${currentTab === "behavioral" ? "Adapid behavioral" : "AppLocker"} events...`}
            />
          ) : displayData.length === 0 ? (
            <div className="no-data-container">
              <h3>No data available</h3>
              <p>
                We couldn't find any activities matching this log description
              </p>
            </div>
          ) : (
            <>
              {/* Conditional rendering based on which view/tab is currently loaded */}
              {currentTab === 'behavioral' ? (
                <AdapidActivityList
                  events={displayData}
                  onSelectEvent={handleSelectEvent}
                />
              ) : (
                <AdapidAppLockerActivityList
                  events={displayData}
                  onSelectEvent={handleSelectEvent}
                />
              )}

              {paginationInfo.totalPages > 1 && (
                <Pagination
                  currentPage={paginationInfo.page}
                  totalPages={paginationInfo.totalPages}
                  totalItems={paginationInfo.totalCount}
                  itemsPerPage={paginationInfo.pageSize}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </div>

        {selectedEvent && (
          <div className="auth-activity-page__detail">
            <EventDetail
              event={selectedEvent}
              loading={detailLoading}
              onClose={handleCloseDetail}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdapidMFA;
